import React from 'react';
import type { Park } from '../types';
import { useApp } from '../context/PetParksContext';
import { MapPin, Star, Clock, Shield, AlertCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { cn } from './ui/utils';

interface ParkCardProps {
  park: Park;
  onClick: () => void;
}

export const ParkCard: React.FC<ParkCardProps> = ({ park, onClick }) => {
  const { t } = useApp();

  return (
    <Card
      className="group cursor-pointer overflow-hidden transition-all hover:shadow-lg hover:shadow-primary/10 active:scale-[0.98] min-w-0"
      onClick={onClick}
    >
      {/* Gradient Accent Bar */}
      <div className="h-1 w-full bg-gradient-to-r from-primary via-purple-500 to-primary" />

      <div className="p-3 sm:p-4 min-w-0">
        {/* Header */}
        <div className="mb-2.5 flex items-start justify-between gap-3 sm:mb-3 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="mb-1 flex items-center gap-2 min-w-0">
              <h3 className="truncate font-semibold leading-tight text-foreground transition-colors group-hover:text-primary min-w-0">
                {park.name}
              </h3>
              {park.verified && (
                <Shield className="h-4 w-4 flex-shrink-0 text-primary" />
              )}
            </div>
            <p className="flex items-start gap-1 text-sm text-muted-foreground min-w-0">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span className="break-words line-clamp-2 min-w-0">
                {park.distance} km • {park.address}
              </span>
            </p>
          </div>
        </div>

        {/* Rating & Status */}
        <div className="mb-2.5 flex flex-wrap items-center gap-2 sm:mb-3 sm:gap-3">
          <div className="flex items-center gap-1">
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            <span className="font-medium text-foreground">
              {park.rating.toFixed(1)}
            </span>
            <span className="text-xs text-muted-foreground sm:text-sm">
              ({park.reviewCount})
            </span>
          </div>

          <Badge
            variant={park.isOpen ? 'default' : 'secondary'}
            className={cn(
              'text-xs',
              park.isOpen
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : ''
            )}
          >
            {park.isOpen ? t('openNow') : t('closed')}
          </Badge>
        </div>

        {/* Key Features */}
        <div className="mb-2.5 flex flex-wrap gap-1.5 sm:mb-3">
          {park.offLeash && (
            <Badge variant="outline" className="text-xs">
              {t('offLeash')}
            </Badge>
          )}
          {park.fullyFenced && (
            <Badge variant="outline" className="text-xs">
              {t('fullyFenced')}
            </Badge>
          )}
          {park.separateAreas && (
            <Badge variant="outline" className="text-xs">
              {t('separateAreas')}
            </Badge>
          )}
          {park.waterFountain && (
            <Badge variant="outline" className="text-xs">
              {t('waterFountain')}
            </Badge>
          )}
          {park.agilityEquipment && (
            <Badge variant="outline" className="text-xs">
              {t('agilityEquipment')}
            </Badge>
          )}
        </div>

        {/* Alerts */}
        {park.alerts.length > 0 && (
          <div className="mb-2.5 rounded-md bg-yellow-50 p-2 dark:bg-yellow-900/20 sm:mb-3 min-w-0">
            <div className="flex items-start gap-2 min-w-0">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-500" />
              <p className="text-xs leading-relaxed text-yellow-800 dark:text-yellow-200 break-words min-w-0">
                {park.alerts[0].message}
              </p>
            </div>
          </div>
        )}

        {/* Hours */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {park.hours}
        </div>
      </div>
    </Card>
  );
};
